import type { KeyboardEvent } from "react";
import { useState, useCallback, useRef, useEffect, useId } from "react";
import { X, Globe, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  COMPOSER_ACTION_CLASS,
  ComposerSendButton,
  ComposerShell,
} from "@/components/chat/thread/ComposerShell";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { IssueChipGroup } from "./DocLink";
import type { IssueChipData } from "./DocLink";
import { FileTypeIcon as DocumentFileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { UPLOADING_STATE } from "@/stores/chat/ChatStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { ChatTextSize } from "./types";
import { AddFileMenu } from "./AddFileMenu";
import { CHAT_ATTACHMENT_PENDING_STATES } from "@/domains/documents/utils/documentState";
import { ComposerStatusLine } from "./ComposerStatusLine";
import type { ChatSendError } from "./useChatSend";
import { isSpaceActivatingFocusedControl } from "./useTypeToFocus";
import {
  CHAT_MESSAGE_COUNTER_THRESHOLD,
  chatMessageLength,
  MAX_CHAT_MESSAGE_LENGTH,
} from "@/stores/chat/messageLimits";

/** Tags whose elements count as "another input is focused" */
const INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
const DOCUMENT_ATTACHMENT_CHIP_CLASS =
  "flex w-fit max-w-full min-w-0 items-start gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1 text-[11px] leading-4 text-neutral-700";

/**
 * WHA-3829: the project this chat is scoped to, shown above the field as a
 * compact marker rather than a full-width row — being scoped is a small piece
 * of state, and a banner across the panel overstated it.
 */
function ProjectScopeChip({
  activeProject,
  onRemoveProject,
}: {
  activeProject: { id: string; name: string };
  onRemoveProject?: () => void;
}) {
  const { t } = useLanguage();
  const scopeLabel = t.documents.chatSidebar.projectScope.active.replace(
    "{name}",
    activeProject.name
  );

  // A live-state marker, not a banner: a lit dot and the project's name, sized
  // to its content. The full sentence stays in the tooltip and in the text the
  // screen reader gets, so nothing is lost by not printing it across the panel.
  // It is carried as real (visually hidden) text rather than an `aria-label`,
  // which a plain span — implicit role=generic — never exposes; the visible
  // name is a truncated copy of the same sentence, so it is hidden from AT.
  return (
    <span
      className={cn(
        "group inline-flex w-fit min-w-0 max-w-full items-center gap-1.5 rounded-full",
        "border border-primary/20 bg-primary/5 py-0.5 pl-2 text-[11px] leading-4 text-neutral-700",
        onRemoveProject ? "pr-1" : "pr-2"
      )}
      data-attachment-row="project"
      title={scopeLabel}
    >
      <span className="sr-only">{scopeLabel}</span>
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
      <span aria-hidden="true" className="min-w-0 truncate font-medium">
        {activeProject.name}
      </span>
      {onRemoveProject && (
        <button
          type="button"
          className="shrink-0 rounded-full p-0.5 text-neutral-400 hover:bg-primary/10 hover:text-neutral-700"
          onClick={onRemoveProject}
          aria-label={t.documents.chatSidebar.projectScope.remove}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/** Web search on/off — the slot the Message Board composer gives to "@". */
function WebSearchToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const { t } = useLanguage();
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(COMPOSER_ACTION_CLASS, enabled && "text-primary hover:text-primary/80")}
            onClick={() => onToggle(!enabled)}
            aria-label={t.documents.chatSidebar.webSearch.toggle}
          >
            <Globe className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-sm font-medium">
            {enabled
              ? t.documents.chatSidebar.webSearch.enabled
              : t.documents.chatSidebar.webSearch.disabled}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Props for SidebarChatInput component.
 */
export interface SidebarChatInputProps {
  /**
   * Callback when user sends a message. Resolve `false` when the send was not
   * accepted: an uncontrolled field then hands the text back. A controlled
   * field (`value`) never clears itself — its owner clears the draft once the
   * send is accepted, and keeps it when it is not.
   */
  onSend: (message: string) => void | boolean | Promise<boolean>;
  /** A send refused before it reached the thread, reported under the field. */
  sendError?: ChatSendError | null;
  onDismissSendError?: () => void;
  /** Whether the chat is currently loading/processing */
  isLoading: boolean;
  /** Responsive text size derived from widget width */
  textSize?: ChatTextSize;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Documents attached as context */
  attachedDocuments?: Array<{ id: string; filename: string; state?: string | null }>;
  /** Remove an attached document */
  onRemoveDocument?: (docId: string) => void;
  /** PM issues attached as context (WHA-3336) */
  attachedIssues?: Array<{ id: number; issue_number: number; title: string }>;
  /** Remove an attached issue */
  onRemoveIssue?: (issueId: number) => void;
  /** Navigate to an attached issue's page (WHA-3469) */
  onIssueClick?: (issue: IssueChipData) => void;
  /** Open the existing project document picker (WHA-3352: "Browse Projects" branch). */
  onBrowseProjectsClick?: () => void;
  /** WHA-3352: trigger the OS file picker for direct upload from computer. */
  onUploadClick?: () => void;
  /** WHA-3829: open the "Work in a project" picker modal. */
  onWorkInProjectClick?: () => void;
  /** WHA-3353: open the chatbot files panel (uploads + Franklin-generated files). */
  onOpenFilesPanel?: () => void;
  /** WHA-3829: the project this chat is scoped to, shown as a removable chip. */
  activeProject?: { id: string; name: string } | null;
  /** WHA-3829: remove the active project scope. */
  onRemoveProject?: () => void;
  /** Whether to show the AI disclaimer below the input */
  showDisclaimer?: boolean;
  /** Whether web search is enabled for this session */
  webSearchEnabled?: boolean;
  /** Callback when web search toggle changes */
  onWebSearchToggle?: (enabled: boolean) => void;
  /** Callback to stop the in-flight stream (WHA-2637) */
  onStop?: () => void;
  /** WHA-2873/WHA-3481: Whether an AskUserQuestion prompt is open. When true
   *  the bottom bar shows a hint placeholder; the question card handles the answer. */
  askUserActive?: boolean;
  /** Text carried over from the collapsed pill — seeds the input on mount. */
  initialValue?: string;
  /**
   * Parent-owned draft. Pass it when the draft must survive being written from
   * outside the field — the panel does, because pressing Stop hands a queued
   * message back to the composer. Omit it and the field keeps its own state,
   * seeded once from `initialValue`.
   */
  value?: string;
  onValueChange?: (value: string) => void;
}

/**
 * SidebarChatInput - Input area component for the sidebar chat.
 *
 * Features:
 * - Textarea input with placeholder "Ask Franklin..."
 * - Enter key sends message (without Shift)
 * - Shift+Enter for newline
 * - Send button with loading state
 * - Disabled state during loading
 * - "+" button to attach documents as context
 * - Document chips above the input when files are attached
 */
export function SidebarChatInput({
  onSend,
  isLoading,
  textSize = "sm",
  disabled = false,
  attachedDocuments = [],
  onRemoveDocument,
  attachedIssues = [],
  onRemoveIssue,
  onIssueClick,
  onBrowseProjectsClick,
  onUploadClick,
  onWorkInProjectClick,
  onOpenFilesPanel,
  activeProject = null,
  onRemoveProject,
  showDisclaimer = false,
  webSearchEnabled,
  onWebSearchToggle,
  onStop,
  askUserActive = false,
  initialValue = "",
  value,
  onValueChange,
  sendError = null,
  onDismissSendError,
}: SidebarChatInputProps) {
  const { t } = useLanguage();
  const [internalInput, setInternalInput] = useState(initialValue);
  const isControlled = value !== undefined;
  const input = isControlled ? value : internalInput;
  const setInput = useCallback(
    (next: string) => {
      if (isControlled) onValueChange?.(next);
      else setInternalInput(next);
    },
    [isControlled, onValueChange]
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const statusId = useId();
  const inputTextClass = textSize === "lg" ? "text-[15px]" : "text-[14px]";

  // Auto-focus: when user types and no other input is focused, redirect to chat textarea
  useEffect(() => {
    function handleGlobalKeyDown(e: globalThis.KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return; // only printable characters
      if (isSpaceActivatingFocusedControl(e)) return;
      // WHA-3481: while a question is open the bottom bar is disabled — don't
      // steal focus into it; the question card handles the answer.
      if (askUserActive) return;
      // WHA-3664: on the 3D viewer (/buildings/:id/model) WASD/arrow keys drive
      // the camera — don't hijack keystrokes into the chat. Read the path at
      // event time (not via useLocation) so the component stays Router-agnostic.
      if (window.location.pathname.endsWith("/model")) return;

      const active = document.activeElement;
      if (active && (INPUT_TAGS.has(active.tagName) || (active as HTMLElement).isContentEditable)) {
        return;
      }

      textareaRef.current?.focus();
    }

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [askUserActive]);

  // WHA-3460: auto-grow the textarea with its content. Reset to `auto`
  // first so it can shrink again, then size to scrollHeight. `max-h` caps
  // growth and `overflow-y-auto` takes over past that. textSize is a dep
  // because it drives inputTextClass (font size → line height).
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [input, textSize]);

  // WHA-3356 / WHA-3364: chip spins while extraction hasn't finished.
  // The pipeline state machine progresses
  //   UNPROCESSED → EXTRACTING → EXTRACTED → CLASSIFYING → CLASSIFIED → …
  //   → TAGGING → TAGGED → STAGING → STAGED → EMBEDDING → EMBEDDED
  // The chatbot can read text the moment state leaves the pre-extraction
  // set below. An allowlist of "resting" states (the old approach) made the
  // spinner flicker back on during every *-ING intermediate stage — so we
  // invert: spin ONLY while state is in this small blocklist.
  //
  // WHA-3330: Send is NOT blocked while extraction is pending. The user
  // can submit at any time; ChatStore.submitMessage stashes the SSE
  // dispatch in a deferred queue when chips are still pre-extraction
  // and fires it the instant every chip is ready. The spinning chip is
  // the wait indicator.
  const PRE_EXTRACTION_STATES = new Set([UPLOADING_STATE, ...CHAT_ATTACHMENT_PENDING_STATES]);
  const isPreExtraction = (state: string | null | undefined): boolean =>
    state != null && PRE_EXTRACTION_STATES.has(state);

  // Two hard gates: `disabled` (there is no session yet, so nowhere for a
  // message to go) and a draft over the length limit, which stays in the field
  // for the user to shorten. A turn already in flight is NOT a gate — the
  // field stays live and `onSend` queues the message behind the running answer
  // rather than refusing it.
  const draftLength = chatMessageLength(input);
  const overLimit = draftLength > MAX_CHAT_MESSAGE_LENGTH;
  const canSend = draftLength > 0 && !disabled && !overLimit;
  // Mirrors when ComposerStatusLine renders, so the field never points
  // `aria-describedby` at an element that is not there.
  const statusVisible = Boolean(sendError) || draftLength >= CHAT_MESSAGE_COUNTER_THRESHOLD;

  const handleSend = useCallback(() => {
    const trimmedInput = input.trim();
    if (!trimmedInput || disabled || trimmedInput.length > MAX_CHAT_MESSAGE_LENGTH) {
      return;
    }

    const accepted = onSend(trimmedInput);
    // A controlled draft belongs to its owner, which clears it only once the
    // send is accepted. Clearing it here too would destroy a refused message.
    if (isControlled) return;
    setInternalInput("");
    void Promise.resolve(accepted).then((result) => {
      if (result !== false) return;
      setInternalInput((current) =>
        current.trim() === trimmedInput
          ? current
          : [trimmedInput, current].filter(Boolean).join("\n\n")
      );
    });
  }, [disabled, input, isControlled, onSend]);

  // The shell wraps `actions` in a flex row but not `leading`, so this run
  // needs its own — and stays undefined when a surface supplies neither
  // control, rather than laying out an empty box.
  const hasAttachMenu = Boolean(
    onBrowseProjectsClick || onUploadClick || onWorkInProjectClick || onOpenFilesPanel
  );
  const hasWebSearch = webSearchEnabled !== undefined && Boolean(onWebSearchToggle);
  const leadingControls =
    hasAttachMenu || hasWebSearch ? (
      <div className="flex flex-shrink-0 items-center gap-0.5">
        {hasAttachMenu && (
          <AddFileMenu
            onUploadClick={onUploadClick}
            onBrowseProjectsClick={onBrowseProjectsClick}
            onWorkInProjectClick={onWorkInProjectClick}
            onOpenFilesPanel={onOpenFilesPanel}
          />
        )}
        {webSearchEnabled !== undefined && onWebSearchToggle && (
          <WebSearchToggle enabled={webSearchEnabled} onToggle={onWebSearchToggle} />
        )}
      </div>
    ) : undefined;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter without Shift sends the message
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      // Shift+Enter allows newline (default behavior)
    },
    [handleSend]
  );

  return (
    <div className="px-4 pb-3 pt-4" data-testid="sidebar-chat-input">
      {/* Attached issue chips — WHA-3468: aggregate into a collapsed
       * "Issue N" chip when more than 2 are attached, mirroring the
       * collapsed doc chip. WHA-3469: clicking a chip opens the issue. */}
      {attachedIssues.length > 0 && (
        <IssueChipGroup
          variant="tray"
          className="mb-1.5 px-1"
          issues={attachedIssues.map((issue) => ({
            id: issue.id,
            issueNumber: issue.issue_number,
            title: issue.title,
          }))}
          onIssueClick={(issue) => onIssueClick?.(issue)}
          onRemove={onRemoveIssue}
        />
      )}

      {activeProject && (
        <div className="mb-1.5 px-1">
          <ProjectScopeChip activeProject={activeProject} onRemoveProject={onRemoveProject} />
        </div>
      )}

      {/* The writing field — the Message Board composer's pill, minus the
       * author avatar. What the user brings to the question ("+" and the
       * web-search globe) leads the field; what they do with it (stop, send)
       * trails it, so the two runs never crowd each other. */}
      <ComposerShell
        style={{ minHeight: 52 }}
        // `px-2` rather than the bare-text inset: the icon run now occupies the
        // room that inset was holding open, on both sides.
        // `items-center`, not the shell default being overridden to `items-end`:
        // both icon runs sit on the field's centre line.
        className="items-center rounded-[20px] px-2 py-2 shadow-sm"
        leading={leadingControls}
        actions={
          <>
            {/* Stop does not replace Send while a turn runs: the user can still
                write the next message, and it queues behind the answer. */}
            {isLoading && onStop && (
              <ComposerSendButton
                canSend
                onClick={onStop}
                label={t.documents.chatSidebar.stopGenerating}
              >
                <Square className="h-3.5 w-3.5 fill-current" />
              </ComposerSendButton>
            )}
            <ComposerSendButton
              canSend={canSend}
              onClick={handleSend}
              label={t.documents.chatSidebar.sendMessage}
              describedBy={overLimit ? statusId : undefined}
            />
          </>
        }
      >
        <div className="flex min-w-0 flex-col">
          {attachedDocuments.length > 0 && (
            <div
              className="flex min-w-0 flex-wrap items-start gap-1.5 pb-1.5 pt-0.5"
              data-testid="document-attachment-chips"
            >
              {attachedDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className={DOCUMENT_ATTACHMENT_CHIP_CLASS}
                  data-attachment-row="document"
                >
                  <DocumentFileTypeIcon
                    filename={doc.filename}
                    className="h-5 w-5"
                    labelClassName="text-[8px] font-normal"
                  />
                  <span className="min-w-0 flex-1 whitespace-normal break-words pt-0.5">
                    {doc.filename}
                  </span>
                  {isPreExtraction(doc.state) ? (
                    <span
                      role="status"
                      aria-label={`Loading ${doc.filename}`}
                      className="flex h-5 w-5 shrink-0 items-center justify-center text-neutral-500"
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    </span>
                  ) : onRemoveDocument ? (
                    <button
                      type="button"
                      onClick={() => onRemoveDocument(doc.id)}
                      className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                      aria-label={`Remove ${doc.filename}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              askUserActive ? "Answer the question above…" : t.documents.chatSidebar.askPlaceholder
            }
            disabled={disabled}
            rows={1}
            style={{ minHeight: 34 }}
            className={cn(
              "max-h-[132px] w-full resize-none overflow-y-auto px-0 py-1.5 leading-[22px]",
              "rounded-none border-0 bg-transparent shadow-none outline-none",
              "text-neutral-900 placeholder:text-neutral-900/60",
              "focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0",
              "focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
              "disabled:cursor-not-allowed disabled:opacity-50",
              inputTextClass
            )}
            aria-label="Chat message input"
            aria-invalid={overLimit || undefined}
            aria-describedby={statusVisible ? statusId : undefined}
          />
        </div>
      </ComposerShell>
      <ComposerStatusLine
        id={statusId}
        length={draftLength}
        sendError={sendError}
        onDismissError={onDismissSendError}
      />
      {showDisclaimer && (
        <p className="mt-1.5 text-center text-[10px] leading-3 text-neutral-500">
          {t.documents.chatSidebar.disclaimerText}
        </p>
      )}
    </div>
  );
}

SidebarChatInput.displayName = "SidebarChatInput";
